import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

const formatHours = (hours) => {
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return '0m';
  const totalMin = Math.round(h * 60);
  const hrs = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hrs <= 0) return `${mins}m`;
  return mins ? `${hrs}h ${mins}m` : `${hrs}h`;
};

const formatMkd = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '0.00 MKD';
  return `${n.toFixed(2)} MKD`;
};

const NAS_SIZE_OPTIONS = [100, 250, 500, 1000, 2000];

// ``ready_no_format`` = DSM provisioning succeeded but the NAS-side
// pre-format was skipped (Synology model can't insmod iscsi_tcp). From
// the operator's POV that's still "ready" — the GPU will do the fast
// format on first attach.
const isReadyStatus = (s) => s === 'ready' || s === 'ready_no_format';

const nasBadgeClass = (status) => {
  const s = String(status || 'idle').toLowerCase();
  if (isReadyStatus(s)) return 'bg-emerald-500/20 text-emerald-200';
  if (s === 'failed' || s === 'error') return 'bg-red-500/20 text-red-200';
  if (s === 'disabled' || s === 'idle') return 'bg-white/10 text-white/60';
  return 'bg-sky-500/20 text-sky-200';
};

const Admin = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery('adminOverview', adminAPI.getOverview, {
    refetchInterval: 15000,
    enabled: !!user?.is_admin,
  });
  const { data: usage } = useQuery('adminUsage', adminAPI.getUsage, {
    refetchInterval: 15000,
    enabled: !!user?.is_admin,
  });
  const { data: nasData } = useQuery('adminNasIscsi', adminAPI.iscsiUsers, {
    // Progress is only interesting when someone is actively provisioning;
    // 10s is a good balance against the DSM API rate limit.
    refetchInterval: 10000,
    enabled: !!user?.is_admin,
  });
  const [nasSizeDraft, setNasSizeDraft] = useState({});
  const [caps, setCaps] = useState({});

  const bulkProvision = useMutation(() => adminAPI.iscsiBulkProvision(), {
    onSuccess: (payload) => {
      queryClient.invalidateQueries('adminNasIscsi');
      toast.success(`Queued ${payload?.count ?? 0} family master libraries`);
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || 'Bulk provision failed'),
  });

  const migrateHousehold = useMutation(
    (sourceUserId) => adminAPI.iscsiMigrateHousehold(sourceUserId),
    {
      onSuccess: (payload) => {
        queryClient.invalidateQueries('adminNasIscsi');
        if (payload?.ok) toast.success(`Moved library to ${payload.master_username}`);
        else toast.error(payload?.reason || 'Could not move library');
      },
      onError: (err) =>
        toast.error(err?.response?.data?.detail || 'Could not move library'),
    },
  );

  const nasToggle = useMutation(
    (body) => adminAPI.iscsiToggle(body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminNasIscsi');
        toast.success('NAS library updated');
      },
      onError: (err) =>
        toast.error(err?.response?.data?.detail || 'Could not update NAS library'),
    },
  );

  useEffect(() => {
    if (usage?.caps) setCaps(usage.caps);
  }, [usage]);

  const saveCaps = useMutation(() => adminAPI.patchCaps(caps), {
    onSuccess: () => {
      queryClient.invalidateQueries('adminUsage');
      toast.success('Caps saved');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || 'Could not save caps');
    },
  });

  if (!user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-red-300">
        {error.response?.data?.detail || 'Could not load admin overview.'}
      </p>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-2">Admin</h1>
        <p className="text-white text-opacity-80">
          {data?.user_count || 0} users · {data?.active_sessions || 0} live sessions
          {usage ? ` · ${usage.live_quad_vms || 0} live 4-seat · ${usage.live_solo_vms || 0} live 1:1` : ''}
        </p>
      </div>

      {usage && (
        <div className="gaming-card rounded-lg p-6 space-y-4">
          <h3 className="text-xl font-semibold text-white">GPU cost watch</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-white">
            <div>
              <p className="text-white/50">Live charged</p>
              <p className="font-semibold">{formatMkd(usage.live_charged_mkd)}</p>
            </div>
            <div>
              <p className="text-white/50">Month charged</p>
              <p className="font-semibold">{formatMkd(usage.month_charged_mkd)}</p>
            </div>
            <div>
              <p className="text-white/50">Month hours</p>
              <p className="font-semibold">{formatHours(usage.month_hours)}</p>
            </div>
            <div>
              <p className="text-white/50">Rough day forecast</p>
              <p className="font-semibold">{formatMkd(usage.forecast_day_mkd)}</p>
            </div>
          </div>
          <form
            className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm"
            onSubmit={(e) => {
              e.preventDefault();
              saveCaps.mutate();
            }}
          >
            {[
              ['max_live_quad_vms', 'Max 4-seat VMs'],
              ['max_live_solo_vms', 'Max 1:1 VMs'],
              ['lobby_grace_seconds', 'Lobby wait (s)'],
              ['month_wallet_cap_mkd', 'Month wallet cap'],
              ['month_hours_cap', 'Month hours cap'],
            ].map(([key, label]) => (
              <label key={key} className="text-white/70">
                {label}
                <input
                  className="mt-1 w-full px-2 py-1 rounded text-black"
                  value={caps[key] ?? ''}
                  onChange={(e) => setCaps({ ...caps, [key]: e.target.value })}
                />
              </label>
            ))}
            <button
              type="submit"
              className="col-span-2 md:col-span-5 bg-violet-600 text-white px-3 py-2 rounded-lg"
              disabled={saveCaps.isLoading}
            >
              Save caps
            </button>
          </form>
          {(usage.lobbies || []).length > 0 && (
            <div>
              <p className="text-white font-medium mb-2">Open lobbies</p>
              <ul className="text-sm text-white/80 space-y-1">
                {usage.lobbies.map((row) => (
                  <li key={row.id}>
                    #{row.id} {row.product} {row.status} · {row.filled}/4
                    {row.invite_code ? ` · ${row.invite_code}` : ''}
                    {row.gpu_type ? ` · ${row.gpu_type}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-white">
              <thead className="text-white/60">
                <tr>
                  <th className="pb-2 pr-3">Session</th>
                  <th className="pb-2 pr-3">SKU</th>
                  <th className="pb-2 pr-3">Seats</th>
                  <th className="pb-2 pr-3">GPU</th>
                  <th className="pb-2 pr-3">Hours</th>
                  <th className="pb-2">Charged</th>
                </tr>
              </thead>
              <tbody>
                {(usage.sessions || []).map((row) => (
                  <tr key={row.id} className="border-t border-white/10">
                    <td className="py-2 pr-3">{row.id} · {row.status}</td>
                    <td className="py-2 pr-3">{row.product_sku || '—'}</td>
                    <td className="py-2 pr-3">{row.filled_seats}/{row.billed_seats}</td>
                    <td className="py-2 pr-3">{row.gpu_type}</td>
                    <td className="py-2 pr-3">{formatHours(row.hours)}</td>
                    <td className="py-2">{formatMkd(row.charged_mkd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="gaming-card rounded-lg p-6 overflow-x-auto">
        <h3 className="text-xl font-semibold text-white mb-4">Users</h3>
        <table className="w-full text-left text-sm text-white">
          <thead className="text-white text-opacity-60">
            <tr>
              <th className="pb-2 pr-3">User</th>
              <th className="pb-2 pr-3">Hours</th>
              <th className="pb-2 pr-3">GPU spend</th>
              <th className="pb-2 pr-3">Wallet</th>
              <th className="pb-2 pr-3">Sub</th>
              <th className="pb-2">Last login</th>
            </tr>
          </thead>
          <tbody>
            {(data?.users || []).map((row) => (
              <tr key={row.id} className="border-t border-white border-opacity-10">
                <td className="py-2 pr-3">
                  {row.username}
                  {row.is_admin ? ' · admin' : ''}
                </td>
                <td className="py-2 pr-3">{formatHours(row.total_hours_played)}</td>
                <td className="py-2 pr-3">{formatMkd(row.total_spent)}</td>
                <td className="py-2 pr-3">{formatMkd(row.wallet_balance)}</td>
                <td className="py-2 pr-3">{row.subscription_active ? 'active' : '—'}</td>
                <td className="py-2">
                  {row.last_login ? new Date(row.last_login).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="gaming-card rounded-lg p-6 overflow-x-auto">
        <div className="flex items-baseline justify-between mb-4 gap-3">
          <h3 className="text-xl font-semibold text-white">NAS storage</h3>
          <button
            type="button"
            onClick={() => bulkProvision.mutate()}
            disabled={bulkProvision.isLoading}
            className="text-xs bg-emerald-600/80 hover:bg-emerald-500 text-white px-3 py-1 rounded disabled:opacity-50"
          >
            {bulkProvision.isLoading ? 'Queuing…' : 'Provision family masters'}
          </button>
          <p className="text-xs text-white/50">
            Solo {nasData?.defaults?.solo_gb ?? 250} GB · Family{' '}
            {nasData?.defaults?.family_gb ?? 500} GB · thin-provisioned on Synology
          </p>
        </div>
        <table className="w-full text-left text-sm text-white">
          <thead className="text-white/60">
            <tr>
              <th className="pb-2 pr-3">User</th>
              <th className="pb-2 pr-3">Status</th>
              <th className="pb-2 pr-3">Size</th>
              <th className="pb-2 pr-3">Progress</th>
              <th className="pb-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {(nasData?.rows || []).map((row) => {
              const draft = nasSizeDraft[row.id];
              const size = draft ?? row.iscsi_size_gb ?? row.default_size_gb;
              const isBusy =
                nasToggle.isLoading &&
                nasToggle.variables?.user_id === row.id;
              return (
                <tr key={row.id} className="border-t border-white/10 align-top">
                  <td className="py-2 pr-3">
                    <div>{row.username}</div>
                    <div className="text-xs text-white/40">
                      {row.public_id}
                      {row.family_id ? ' · family' : ' · solo'}
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${nasBadgeClass(
                        row.iscsi_status,
                      )}`}
                    >
                      {row.iscsi_status || 'idle'}
                    </span>
                    {row.iscsi_last_error ? (
                      <div className="text-xs text-red-300 mt-1 max-w-xs truncate" title={row.iscsi_last_error}>
                        {row.iscsi_last_error}
                      </div>
                    ) : null}
                  </td>
                  <td className="py-2 pr-3">
                    <select
                      value={size}
                      onChange={(e) =>
                        setNasSizeDraft({
                          ...nasSizeDraft,
                          [row.id]: Number(e.target.value),
                        })
                      }
                      className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm"
                    >
                      {NAS_SIZE_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n >= 1000 ? `${n / 1000} TB` : `${n} GB`}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 pr-3 w-64">
                    <div className="h-2 rounded bg-white/10 overflow-hidden">
                      <div
                        className={`h-full ${isReadyStatus(row.iscsi_status) ? 'bg-emerald-400' : 'bg-sky-400'}`}
                        style={{ width: `${Math.max(0, Math.min(100, row.iscsi_progress_pct || 0))}%` }}
                      />
                    </div>
                    <div className="text-xs text-white/50 mt-1">
                      {row.iscsi_progress_label || (row.iscsi_enabled ? '…' : 'off')}
                      {isReadyStatus(row.iscsi_status) && row.iscsi_size_gb ? ` · ${row.iscsi_size_gb} GB` : ''}
                    </div>
                  </td>
                  <td className="py-2">
                    {row.iscsi_enabled ? (
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() =>
                            nasToggle.mutate({
                              user_id: row.id,
                              enabled: true,
                              size_gb: size,
                            })
                          }
                          className="text-xs bg-sky-600/80 hover:bg-sky-500 text-white px-2 py-1 rounded disabled:opacity-50"
                        >
                          Retry / resize
                        </button>
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() =>
                            nasToggle.mutate({ user_id: row.id, enabled: false })
                          }
                          className="text-xs bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded disabled:opacity-50"
                        >
                          Disable
                        </button>
                        {row.family_seat > 0 && row.iscsi_target_iqn ? (
                          <button
                            type="button"
                            disabled={migrateHousehold.isLoading}
                            onClick={() => migrateHousehold.mutate(row.id)}
                            className="text-xs bg-fuchsia-600/80 hover:bg-fuchsia-500 text-white px-2 py-1 rounded disabled:opacity-50"
                          >
                            Move to household
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() =>
                          nasToggle.mutate({
                            user_id: row.id,
                            enabled: true,
                            size_gb: size,
                          })
                        }
                        className="text-xs bg-emerald-600/80 hover:bg-emerald-500 text-white px-3 py-1 rounded disabled:opacity-50"
                      >
                        Enable
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="gaming-card rounded-lg p-6 overflow-x-auto">
        <h3 className="text-xl font-semibold text-white mb-4">Sessions</h3>
        <table className="w-full text-left text-sm text-white">
          <thead className="text-white text-opacity-60">
            <tr>
              <th className="pb-2 pr-3">User</th>
              <th className="pb-2 pr-3">Session</th>
              <th className="pb-2 pr-3">GPU</th>
              <th className="pb-2 pr-3">Status</th>
              <th className="pb-2 pr-3">Hours</th>
              <th className="pb-2">Cost</th>
            </tr>
          </thead>
          <tbody>
            {(data?.sessions || []).map((row) => (
              <tr key={row.id} className="border-t border-white border-opacity-10">
                <td className="py-2 pr-3">{row.username}</td>
                <td className="py-2 pr-3">{row.session_name}</td>
                <td className="py-2 pr-3">{row.gpu_type}</td>
                <td className="py-2 pr-3">{row.status}</td>
                <td className="py-2 pr-3">{formatHours(row.total_hours)}</td>
                <td className="py-2">{formatMkd(row.total_cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Admin;
